import { useEffect, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { Loader } from '@/ui'
import { getIsFetchingOrNotStarted, selectFetchSuccess } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'

import { fetchIntake } from './compliance.actions'
import { selectAnswers } from './compliance.selectors'
import { COMPLIANCE_PATHS, FETCH_INTAKE_KEY } from './lib/constants'
import { complianceVerdict, hasVerdictInputs } from './lib/qualification'

/**
 * Keeps a practice Heard cannot serve out of checkout. The report's closing card already
 * withholds the link; this closes the typed-URL path behind it.
 *
 * Fetches the intake itself — nothing else on this route populates the answers slice, and
 * without it the verdict is always null and the gate never fires.
 *
 * Three destinations, because "no verdict" means two different things:
 * - a decided rejection sends them back to the report, which explains why;
 * - an intake that *loaded* without enough answers to decide is not a rejection, but it
 *   is not a licence to buy either — send them back to finish it;
 * - a fetch that *failed* falls through to checkout. Failing open there is deliberate:
 *   a blip must never read as a rejection, and must never bounce a paying customer out
 *   of checkout.
 *
 * While the fetch is still in flight nothing renders but a loader — checkout must not
 * flash for a practice the resolved verdict is about to turn away. A verdict already in
 * the store redirects immediately rather than waiting on the refetch.
 */
export const QualifiedOnlyRoute = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const dispatch = useAppDispatch()
  const answers = useReselector(selectAnswers)
  const intakeLoaded = useReselector(selectFetchSuccess, FETCH_INTAKE_KEY)
  const intakeInFlight = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_INTAKE_KEY
  )

  useEffect(() => {
    dispatch(fetchIntake())
  }, [dispatch])

  const verdict = complianceVerdict(answers)

  if (
    verdict?.treatment === 'disqualified' ||
    verdict?.treatment === 'waitlist'
  )
    return <Navigate to={COMPLIANCE_PATHS.report} replace />

  if (intakeLoaded && !hasVerdictInputs(answers))
    return <Navigate to={COMPLIANCE_PATHS.intake} replace />

  if (intakeInFlight)
    return (
      <div className="flex w-full items-center justify-center py-24">
        <Loader size={40} role="status" aria-label="Loading" />
      </div>
    )

  return children
}
